import type {
  ChangeFieldType,
  CreateFieldDefinitionInput,
  CreateFieldOption,
  FieldDefinition,
  FieldOption,
  Job,
  Page,
  ProjectFieldBinding,
  PutFieldBindings,
  Reorder,
  UpdateFieldDefinition,
  UpdateFieldOption,
} from '@servicetim/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { adminPath, useApiContext } from './client';
import { DEFAULT_PAGE_SIZE, pageQuery, usePagedList } from './paging';

const fieldsBase = adminPath('/fields');
const projectFieldsPath = (projectId: string) => adminPath(`/projects/${projectId}/fields`);
const bindingsPath = (projectId: string) => adminPath(`/projects/${projectId}/field-bindings`);

export interface FieldListFilters {
  scope?: 'tenant' | 'project';
  projectId?: string;
}

/** `GET /admin/v1/fields?scope=&projectId=` (tenant admin; every definition in the tenant). */
export function useFields(filters: FieldListFilters = {}, pageSize = DEFAULT_PAGE_SIZE) {
  const { request, keys } = useApiContext();
  return usePagedList<FieldDefinition>({
    queryKey: keys.fields.list({ ...filters, pageSize }),
    fetchPage: (cursor, signal) =>
      request<Page<FieldDefinition>>('GET', fieldsBase, {
        query: { ...filters, ...pageQuery(cursor, pageSize) },
        signal,
      }),
  });
}

/** `GET /admin/v1/projects/{id}/fields`: project-scoped + tenant-scoped definitions visible to the project. */
export function useProjectFields(projectId: string | undefined, pageSize = 200) {
  const { request, keys } = useApiContext();
  return usePagedList<FieldDefinition>({
    queryKey: keys.project(projectId ?? '').fields({ pageSize }),
    fetchPage: (cursor, signal) =>
      request<Page<FieldDefinition>>('GET', projectFieldsPath(projectId ?? ''), {
        query: pageQuery(cursor, pageSize),
        signal,
      }),
    enabled: projectId !== undefined,
  });
}

/** `GET /admin/v1/fields/{fid}` */
export function useField(fieldId: string | undefined) {
  const { request, keys } = useApiContext();
  return useQuery({
    queryKey: keys.fields.detail(fieldId ?? ''),
    queryFn: ({ signal }) =>
      request<FieldDefinition>('GET', `${fieldsBase}/${fieldId}`, { signal }),
    enabled: fieldId !== undefined,
  });
}

/**
 * `POST /admin/v1/fields` (tenant scope) or `POST /admin/v1/projects/{id}/fields` (project scope).
 * Pass `projectId` for a project-scoped field.
 */
export function useCreateField() {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: ({ projectId, ...input }: CreateFieldDefinitionInput & { projectId?: string }) =>
      request<FieldDefinition>('POST', projectId ? projectFieldsPath(projectId) : fieldsBase, {
        body: input,
      }),
    onSuccess: (_field, { projectId }) =>
      invalidate(keys.fields.all, ...(projectId ? [keys.project(projectId).scope] : [])),
  });
}

/** `PATCH /admin/v1/fields/{fid}` */
export function useUpdateField() {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdateFieldDefinition }) =>
      request<FieldDefinition>('PATCH', `${fieldsBase}/${id}`, { body: patch }),
    onSuccess: (field) => invalidateField(field),
  });

  function invalidateField(field: FieldDefinition) {
    return invalidate(
      keys.fields.all,
      ...(field.projectId ? [keys.project(field.projectId).scope] : []),
    );
  }
}

/** `POST /admin/v1/fields/{fid}/archive` / `unarchive` */
export function useSetFieldArchived() {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: ({ id, archived }: { id: string; archived: boolean }) =>
      request<FieldDefinition>(
        'POST',
        `${fieldsBase}/${id}/${archived ? 'archive' : 'unarchive'}`,
        {
          body: {},
        },
      ),
    onSuccess: (field) =>
      invalidate(
        keys.fields.all,
        ...(field.projectId ? [keys.project(field.projectId).scope] : []),
      ),
  });
}

/** `POST /admin/v1/fields/{fid}/promote`: project scope → tenant scope (tenant admin). */
export function usePromoteField() {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: (id: string) =>
      request<FieldDefinition>('POST', `${fieldsBase}/${id}/promote`, { body: {} }),
    onSuccess: () => invalidate(keys.fields.all, keys.projects.all),
  });
}

/** `POST /admin/v1/fields/{fid}/change-type` (whitelisted widenings; may return a job). */
export function useChangeFieldType() {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: ({ id, ...input }: ChangeFieldType & { id: string }) =>
      request<{ field: FieldDefinition; job: Job | null } | FieldDefinition>(
        'POST',
        `${fieldsBase}/${id}/change-type`,
        { body: input },
      ),
    onSuccess: () => invalidate(keys.fields.all, keys.jobs.all),
  });
}

/* ----------------------------- options ----------------------------- */

/** `GET /admin/v1/fields/{fid}/options` (array; includes archived, ordered by position). */
export function useFieldOptions(fieldId: string | undefined) {
  const { request, keys } = useApiContext();
  return useQuery({
    queryKey: keys.fields.options(fieldId ?? ''),
    queryFn: ({ signal }) =>
      request<FieldOption[]>('GET', `${fieldsBase}/${fieldId}/options`, { signal }),
    enabled: fieldId !== undefined,
  });
}

export function useCreateFieldOption(fieldId: string) {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: (input: CreateFieldOption) =>
      request<FieldOption>('POST', `${fieldsBase}/${fieldId}/options`, { body: input }),
    onSuccess: () => invalidate(keys.fields.options(fieldId)),
  });
}

export function useUpdateFieldOption(fieldId: string) {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdateFieldOption }) =>
      request<FieldOption>('PATCH', `${fieldsBase}/${fieldId}/options/${id}`, { body: patch }),
    onSuccess: () => invalidate(keys.fields.options(fieldId)),
  });
}

/** `DELETE .../options/{oid}`: archives the option (values keep referencing it). */
export function useArchiveFieldOption(fieldId: string) {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: (id: string) =>
      request<FieldOption | undefined>('DELETE', `${fieldsBase}/${fieldId}/options/${id}`),
    onSuccess: () => invalidate(keys.fields.options(fieldId)),
  });
}

/** `POST .../options/order` with the complete id list. */
export function useReorderFieldOptions(fieldId: string) {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: (input: Reorder) =>
      request<FieldOption[]>('POST', `${fieldsBase}/${fieldId}/options/order`, { body: input }),
    onSuccess: () => invalidate(keys.fields.options(fieldId)),
  });
}

/* ----------------------------- bindings ----------------------------- */

/** `GET /admin/v1/projects/{id}/field-bindings` (array joined with field definitions). */
export function useFieldBindings(projectId: string | undefined) {
  const { request, keys } = useApiContext();
  return useQuery({
    queryKey: keys.project(projectId ?? '').bindings,
    queryFn: ({ signal }) =>
      request<ProjectFieldBinding[]>('GET', bindingsPath(projectId ?? ''), { signal }),
    enabled: projectId !== undefined,
  });
}

/** `PUT /admin/v1/projects/{id}/field-bindings`: the complete list, replaced atomically. */
export function usePutFieldBindings(projectId: string) {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: (bindings: PutFieldBindings) =>
      request<ProjectFieldBinding[]>('PUT', bindingsPath(projectId), { body: bindings }),
    onSuccess: () =>
      invalidate(
        keys.project(projectId).bindings,
        keys.project(projectId).formsAll,
        keys.projects.detail(projectId),
      ),
  });
}
